import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { scroller } from 'react-scroll';

import PriceDisplay from '../../../../components/global/PriceDisplay';
import FreePassPromoCodeLabel from '../../../../components/global/FreePassPromoCodeLabel';
import Button from '../../../../components/global/Button';
import getConfig from '../../../../util/config';
import { gtm } from '../../../../util/analytics';
import { STRIPE_CHECKOUT_MODAL } from '../../../../components/global/StripeCheckout';
import useOpenModal from '../../../../hooks/useOpenModal';
import { CURRENCY } from '../../../../constants/currency';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    addresses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
  }),
  loading: PropTypes.bool,
  finalPrice: PropTypes.number,
  totalPrice: PropTypes.number,
  onStripeToken: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  blockers: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      condition: PropTypes.bool.isRequired,
    }),
  ),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  checkoutItems: PropTypes.object,
  printPartnerShippingOptionId: PropTypes.string,
  allowedPromoCodes: PropTypes.array,
  defaultPromoCode: PropTypes.string,
  loadingDefaultPromoCode: PropTypes.bool,
  stripeModalProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
  currency: PropTypes.oneOf(Object.values(CURRENCY)),
};

const CheckoutCartPaymentPanel = ({
  location,
  account,
  loading,
  finalPrice,
  totalPrice,
  onStripeToken,
  submitting,
  blockers,
  checkoutItems,
  printPartnerShippingOptionId,
  allowedPromoCodes = [],
  defaultPromoCode,
  // Opening the Stripe modal before the free pass code is verified would
  // start checkout without it, so the panel waits like it does for pricing.
  loadingDefaultPromoCode = false,
  stripeModalProps = {},
  currency = CURRENCY.USD,
}) => {
  const openModal = useOpenModal();
  const isLoading = loading || loadingDefaultPromoCode;
  const [renderBlocker, setRenderBlocker] = useState(false);
  const [blocker] = blockers;
  const { baseUrl } = getConfig()?.app ?? {};

  return (
    <div className="checkout-cart-payment-panel">
      <div className="checkout-cart-payment-panel-price">
        {/* A promo code that makes the order free still shows the struck
            through total next to "Free". */}
        {(finalPrice > 0 || totalPrice > finalPrice) && (
          <Fragment>
            <span>Total</span>
            <PriceDisplay
              price={finalPrice}
              oldPrice={totalPrice}
              currency={currency}
              showDiscount
              animatedStrikethrough
              loading={loading}
              trimDecimalZeros={false}
              freeLabel={
                defaultPromoCode ? (
                  <FreePassPromoCodeLabel promoCode={defaultPromoCode} />
                ) : (
                  'Free'
                )
              }
            />
          </Fragment>
        )}
      </div>

      <div className="ml-auto text-right">
        {blocker && renderBlocker && (
          <p className="text-sm text-danger mb-1">{blocker.message}</p>
        )}

        <Button
          data-testid="checkout-cart-payment-panel-submit"
          className={classNames({
            'checkout-cart-payment-panel-submit': true,
            'is-loading': isLoading || submitting,
            disabled: !!blocker,
          })}
          disabled={isLoading || submitting}
          onClick={event => {
            setRenderBlocker(false);

            // If there's a blocker condition (meaning the user should not check out)
            // prevent check out and show the blocker's message.
            if (blocker) {
              setRenderBlocker(true);

              if (blocker.scrollElement) {
                scroller.scrollTo(blocker.scrollElement, {
                  duration: 500,
                  smooth: true,
                  offset: 50,
                });
              }

              event.preventDefault();
              event.stopPropagation();
              return;
            }

            gtm?.trackPageView({
              url: `${baseUrl}${location.pathname}/virtual/payment`,
              title: 'Virtual Pageview - Payment',
              userId: account?.id,
            });

            openModal(STRIPE_CHECKOUT_MODAL, {
              onSuccess: onStripeToken,
              cardRequired: false,
              // The cart total already includes the default code, so a $0
              // total still needs the promo input for the modal to apply it.
              accountReferralTypes:
                finalPrice > 0 || defaultPromoCode ? allowedPromoCodes : [],
              checkoutPriceVariables: ({ promo_code: promoCode }) => ({
                ...(checkoutItems ?? {}),
                account_id: account?.id,
                address_id: account?.addresses?.[0]?.id,
                print_partner_shipping_option_id: printPartnerShippingOptionId,
                promo_code: promoCode,
              }),
              defaultName: account?.name ?? '',
              defaultEmail: account?.email_address ?? '',
              freeButtonLabel: 'Complete order',
              // A free pass code found for the event. Explicit
              // `stripeModalProps.defaultPromoCode` (prepaid flows) wins.
              defaultPromoCode,
              ...stripeModalProps,
            });
          }}
        >
          {isLoading || submitting ? 'Loading…' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};

CheckoutCartPaymentPanel.propTypes = propTypes;

export default CheckoutCartPaymentPanel;
